import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Check, ArrowLeft, Scan, PersonSimpleRun, Barbell, Clock, Star,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";

const ENQUIRY_EMAIL = "Erin@otofitness.co.nz";
const GM_SIGNUP = "https://otorohangahf.gymmasteronline.com/portal/signup";
const PT_BOOKING_URL = "https://form.jotform.com/Erin_Milne/form";
const GM_LOGIN = "https://otorohangahf.gymmasteronline.com/portal/login";
const openGM = (u) => window.open(u, "_blank", "noopener,noreferrer");

const serviceIcon = (t) => (t === "body_scan" ? Scan : t === "pt" ? PersonSimpleRun : Barbell);

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.07 } }),
};

export default function Memberships() {
  const [plans, setPlans] = useState([]);
  const [services, setServices] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const purchase = (kind, item) => {
    // All membership & payment sign-ups happen on GymMaster; members sync back via the API.
    openGM(GM_SIGNUP);
  };

  const byPrice = (a, b) => a.price - b.price;
  const ongoing = plans.filter((p) => p.category === "ongoing").sort(byPrice);
  const prepaid = plans.filter((p) => p.category === "prepaid").sort(byPrice);
  const passes = plans.filter((p) => p.category === "pass").sort(byPrice);

  return (
    <div className="bg-white text-zinc-950 min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-zinc-200/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <BrandLogo testId="mem-logo" onClick={() => navigate("/")} />
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="hidden sm:inline-flex items-center gap-2 text-sm font-bold hover:text-[#FF4F00] transition-colors" data-testid="mem-back">
              <ArrowLeft size={16} weight="bold" /> Home
            </button>
            {user ? (
              <button onClick={() => navigate("/dashboard")} data-testid="mem-account" className="bg-zinc-950 text-white rounded-sm px-5 py-2.5 text-sm font-bold hover:bg-zinc-800 transition-colors">My Account</button>
            ) : (
              <button onClick={() => openGM(GM_LOGIN)} data-testid="mem-signin" className="bg-[#FF4F00] text-white rounded-sm px-5 py-2.5 text-sm font-bold hover:bg-[#E04600] transition-colors">Member sign in</button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4F00] mb-3">Memberships & Services</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none max-w-3xl">
          Pick the plan that fits your training.
        </h1>
        <p className="mt-5 text-lg text-zinc-600 max-w-xl">
          Every membership includes 24/7 gym access, showers and the locker room. No contracts, no games — train your way.
        </p>
      </section>

      {/* Ongoing */}
      <PlanSection id="ongoing" label="Ongoing" title="Weekly & Fortnightly" subtitle="Flexible, cancel anytime.">
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
          {ongoing.map((p, i) => (
            <PlanCard key={p.id} plan={p} i={i} onBuy={() => purchase("plan", p)} testid={`plan-${p.id}`} />
          ))}
        </div>
      </PlanSection>

      {/* Prepaid */}
      <PlanSection id="prepaid" label="Full Cost" title="Pay upfront & save" subtitle="One payment, no weekly billing." dark>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {prepaid.map((p, i) => (
            <PlanCard key={p.id} plan={p} i={i} onBuy={() => purchase("plan", p)} testid={`plan-${p.id}`} compact />
          ))}
        </div>
      </PlanSection>

      {/* Passes */}
      <PlanSection id="passes" label="Casual" title="Passes for visitors" subtitle="Just passing through Otorohanga? Drop in.">
        <div className="grid sm:grid-cols-2 gap-6 max-w-3xl">
          {passes.map((p, i) => (
            <PlanCard key={p.id} plan={p} i={i} onBuy={() => purchase("plan", p)} testid={`plan-${p.id}`} pass />
          ))}
        </div>
      </PlanSection>

      {/* Services */}
      <PlanSection id="services" label="Extras" title="Services you can add" subtitle="Book & pay for coaching and body-composition tracking.">
        <div className="grid md:grid-cols-2 gap-6">
          {services.map((s, i) => {
            const Icon = serviceIcon(s.type);
            return (
              <motion.div key={s.id} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
                className="bg-white border border-zinc-200 rounded-sm p-7 flex flex-col md:flex-row gap-6 items-start hover-lift" data-testid={`service-${s.id}`}>
                <div className="w-12 h-12 rounded-sm bg-[#FF4F00] flex items-center justify-center text-white shrink-0"><Icon size={24} weight="bold" /></div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-black">{s.name}</h3>
                  <p className="text-sm text-zinc-500 mt-1">{s.description}</p>
                  <div className="mt-3 flex items-center gap-3">
                    <span className="font-heading text-2xl font-black">${s.price}</span>
                    <span className="text-sm text-zinc-400">· {s.duration}</span>
                  </div>
                  <button onClick={() => openGM(s.type === "pt" ? PT_BOOKING_URL : GM_SIGNUP)} data-testid={`buy-service-${s.id}`}
                    className="mt-4 inline-flex items-center gap-2 bg-zinc-950 text-white rounded-sm px-5 py-2.5 font-bold hover:bg-zinc-800 transition-colors">
                    Book & Pay <ArrowRight size={16} weight="bold" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </PlanSection>

      <footer className="bg-zinc-950 text-white py-14 mt-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="font-heading font-black text-lg">OTOROHANGA<span className="text-[#FF4F00]">.</span> HEALTH & FITNESS</div>
          <p className="text-sm text-zinc-500">Questions? Email {ENQUIRY_EMAIL} or call 021 154 1728</p>
        </div>
      </footer>
    </div>
  );
}

const PlanSection = ({ id, label, title, subtitle, children, dark }) => (
  <section id={id} className={dark ? "bg-zinc-50 border-y border-zinc-200 py-16 my-4" : "py-16"}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4F00] mb-2">{label}</p>
        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">{title} <span className="text-[#FF4F00]">(scroll for more)</span></h2>
        <p className="text-zinc-500 mt-1">{subtitle}</p>
      </div>
      {children}
    </div>
  </section>
);

const PlanCard = ({ plan, i, onBuy, testid, compact, pass }) => {
  const enquire = !plan.price || plan.price <= 0;
  const highlight = plan.recommended;
  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      variants={fade}
      custom={i}
      className={`relative rounded-sm p-7 flex flex-col ${highlight ? "bg-zinc-950 text-white" : "bg-white border border-zinc-200"}`}
      data-testid={testid}
    >
      {highlight && (
        <span className="self-start inline-flex items-center gap-1 bg-[#FF4F00] text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
          <Star size={12} weight="fill" /> Best value
        </span>
      )}
      <h3 className="font-heading text-2xl font-black">{plan.name}</h3>
      <p className={`text-sm mt-1 ${highlight ? "text-zinc-400" : "text-zinc-500"}`}>{plan.description}</p>
      <div className="mt-5 flex items-end gap-1">
        {enquire ? (
          <span className="font-heading text-3xl font-black">Enquire</span>
        ) : (
          <>
            <span className="font-heading text-5xl font-black">${plan.price}</span>
            <span className={`mb-1.5 text-sm ${highlight ? "text-zinc-400" : "text-zinc-500"}`}>/{plan.interval}</span>
          </>
        )}
      </div>
      {!compact && (
        <ul className="mt-5 space-y-2.5 flex-1">
          {plan.features?.map((f, fi) => (
            <li key={fi} className="flex items-start gap-2 text-sm">
              <Check size={16} weight="bold" className="text-[#FF4F00] mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}
      {compact && <div className="flex-1 mt-3 text-sm text-zinc-500">{plan.features?.[0]}</div>}
      <button
        onClick={onBuy}
        data-testid={`buy-${plan.id}`}
        className={`mt-6 rounded-sm py-3 font-bold tracking-wide transition-colors ${highlight ? "bg-[#FF4F00] text-white hover:bg-[#E04600]" : "bg-zinc-950 text-white hover:bg-zinc-800"}`}
      >
        {enquire ? "Enquire in-store" : pass ? "Get pass" : "Choose plan"}
      </button>
    </motion.div>
  );
};
